import React from 'react';
import { FlowNode } from '../../../../types/flowBuilder';
import { BaseConfigProps, ConfigSection, ConfigField } from '../shared/ConfigFormHelpers';

interface RandomGeneratorConfigProps extends BaseConfigProps {
  node: FlowNode;
  updateConfig: (key: string, value: any) => void;
}

const RandomGeneratorConfig: React.FC<RandomGeneratorConfigProps> = ({ node, updateConfig }) => {
  return (
    <div className="space-y-4">
      <ConfigSection title="Véletlen generálás">
        <ConfigField
          label="Generálás típusa"
          description="Mit szeretnél generálni"
        >
          <select
            value={node.data.config?.type || 'number'}
            onChange={(e) => updateConfig('type', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
          >
            <option value="number">Szám</option>
            <option value="integer">Egész szám</option>
            <option value="float">Tizedes szám</option>
            <option value="boolean">Igaz/Hamis</option>
            <option value="string">Szöveg</option>
            <option value="uuid">UUID</option>
            <option value="color">Szín (hex)</option>
            <option value="choice">Választás listából</option>
          </select>
        </ConfigField>

        {(node.data.config?.type === 'number' || node.data.config?.type === 'integer' || node.data.config?.type === 'float') && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <ConfigField
                label="Minimum"
                description="Alsó határ"
              >
                <input
                  type="number"
                  value={node.data.config?.min || 0}
                  onChange={(e) => updateConfig('min', parseFloat(e.target.value))}
                  step={node.data.config?.type === 'float' ? '0.1' : '1'}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                />
              </ConfigField>

              <ConfigField
                label="Maximum"
                description="Felső határ"
              >
                <input
                  type="number"
                  value={node.data.config?.max || 100}
                  onChange={(e) => updateConfig('max', parseFloat(e.target.value))}
                  step={node.data.config?.type === 'float' ? '0.1' : '1'}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                />
              </ConfigField>
            </div>

            {node.data.config?.type === 'float' && (
              <ConfigField
                label="Tizedesjegyek"
                description="Hány tizedesjegyre kerekítsen"
              >
                <input
                  type="number"
                  value={node.data.config?.decimals || 2}
                  onChange={(e) => updateConfig('decimals', parseInt(e.target.value))}
                  min="0"
                  max="10"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                />
              </ConfigField>
            )}
          </>
        )}

        {node.data.config?.type === 'string' && (
          <>
            <ConfigField
              label="Hossz"
              description="A generált szöveg hossza"
            >
              <input
                type="number"
                value={node.data.config?.length || 10}
                onChange={(e) => updateConfig('length', parseInt(e.target.value))}
                min="1"
                max="1000"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
              />
            </ConfigField>

            <ConfigField
              label="Karakterkészlet"
              description="Milyen karaktereket használjon"
            >
              <select
                value={node.data.config?.charset || 'alphanumeric'}
                onChange={(e) => updateConfig('charset', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
              >
                <option value="alphanumeric">Betűk és számok (a-z, A-Z, 0-9)</option>
                <option value="alpha">Csak betűk (a-z, A-Z)</option>
                <option value="numeric">Csak számok (0-9)</option>
                <option value="lowercase">Kisbetűk (a-z)</option>
                <option value="uppercase">Nagybetűk (A-Z)</option>
                <option value="hex">Hexadecimális (0-9, a-f)</option>
              </select>
            </ConfigField>
          </>
        )}

        {node.data.config?.type === 'choice' && (
          <ConfigField
            label="Választási lehetőségek"
            description="Soronként egy lehetőség"
          >
            <textarea
              value={node.data.config?.choices || ''}
              onChange={(e) => updateConfig('choices', e.target.value)}
              placeholder="Opció 1&#10;Opció 2&#10;Opció 3"
              rows={5}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white font-mono text-sm"
            />
          </ConfigField>
        )}

        <ConfigField
          label="Változó név"
          description="Az eredmény elérhető lesz ezen a néven"
        >
          <input
            type="text"
            value={node.data.config?.variableName || 'random_value'}
            onChange={(e) => updateConfig('variableName', e.target.value)}
            placeholder="random_value"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
          />
        </ConfigField>
      </ConfigSection>

      <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-md p-3">
        <div className="text-xs text-purple-700 dark:text-purple-300">
          <strong>🎲 Használati példák:</strong>
          <br />• <strong>UUID:</strong> Egyedi azonosítók generálása
          <br />• <strong>Szám:</strong> Véletlen sorrend, súlyozás
          <br />• <strong>Választás:</strong> A/B tesztelés, véletlen kiválasztás
          <br />• <strong>Szöveg:</strong> Token, jelszó generálás
        </div>
      </div>
    </div>
  );
};

export default RandomGeneratorConfig;
